import React, { useState, useEffect } from 'react';
import { fetchSales, fetchCustomers, saveSale, deleteSale } from '../../services/api';
import './CreditSale.css';

const today = '04-12-2025';

export default function CreditSale({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [payAmt, setPayAmt] = useState('');
  const [form, setForm] = useState({
    date: formatDateForUI(new Date()),
    customer_id: '',
    product: 'HSD',
    qty: '',
    rate: '',
    bill_no: ''
  });

  function formatDateForUI(d) {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  }

  useEffect(() => {
    loadInitialData();
    loadData();
  }, [companyId]);

  const loadInitialData = async () => {
    try {
      const res = await fetchCustomers(companyId);
      if (res.success) setCustomers(res.Response || []);
    } catch (err) {
      console.error("Customers fetch error:", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchSales({
        Companyid: companyId,
        Name: "",
        Product: "",
        FromDate: "01-01-2024",
        ToDate: formatDateForAPI(new Date())
      });
      if (res.success) {
        const raw = res.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        const creditList = list.filter(r => (r.PaymentMode || r.payment_mode || '').toUpperCase() === 'CREDIT');
        setRecords(creditList.map(r => ({
          id: r.IDS || r.Recid || r.Saleid,
          date: r.Date1 || r.date,
          party: r.Customer || r.customer || '—',
          product: r.Product || r.product,
          qty: parseFloat(r.Qty || r.qty || 0),
          rate: parseFloat(r.Rate || r.rate || 0),
          amount: parseFloat(r.Amount || r.amount || 0),
          paid: parseFloat(r.PaidAmount || 0),
          balance: parseFloat(r.Amount || 0) - parseFloat(r.PaidAmount || 0)
        })));
      }
    } catch (err) {
      console.error("Credit sales fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  function formatDateForAPI(d) {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [day, month, year].join('-');
  }

  const handleSave = async () => {
    if (!form.customer_id || !form.qty || !form.rate) return;

    try {
      const customer = customers.find(c => (c.Customerid || c.id) == form.customer_id);
      const amount = parseFloat(form.qty) * parseFloat(form.rate);

      const payload = {
        Companyid: companyId,
        BillNo: form.bill_no || "0",
        Customerid: form.customer_id,
        Name: customer ? (customer.Name || customer.name) : "Cash",
        Product: form.product,
        Qty: form.qty.toString(),
        Rate: form.rate.toString(),
        Amount: amount.toString(),
        PaymentMode: "Credit",
        Date1: formatDateForAPI(new Date(form.date)),
        IDS: "0"
      };

      const result = await saveSale(payload);
      if (result.success || result.Response === "INSERTED") {
        await loadData();
        setShowForm(false);
        setForm({
          date: formatDateForUI(new Date()),
          customer_id: '',
          product: 'HSD',
          qty: '',
          rate: '',
          bill_no: ''
        });
      } else {
        alert(result.message || "Failed to save credit sale");
      }
    } catch (err) {
      console.error("Credit sale save error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const result = await deleteSale(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadData();
      } else {
        alert(result.message || "Failed to delete record");
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const applyPayment = (id) => {

    const amt = parseFloat(payAmt) || 0;

    if (!amt) return;

    setRecords(prev =>
      prev.map(r => {

        if (r.id !== id) return r;

        const newPaid = Math.min((+r.paid || 0) + amt, +r.amount || 0);

        return {
          ...r,
          paid: newPaid,
          balance: (+r.amount || 0) - newPaid
        };

      })
    );

    setShowPayment(null);
    setPayAmt('');

  };

  const totalBalance = records.reduce((s, r) => s + (+r.balance || 0), 0);

  return (

    <div className="credit-sale-page">

      <div className="page-header">

        <h2 className="page-title">Credit Sales</h2>

        <div style={{ display: 'flex', gap: 8 }}>

          <div className="total-badge">
            Outstanding: <strong>₹{totalBalance.toLocaleString()}</strong>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowForm(!showForm)}
          >
            ＋ New Credit Sale
          </button>

        </div>

      </div>

      {showForm && (

        <div className="section-card">

          <div className="section-header">New Credit Sale</div>

          <div className="form-row">

            <div className="form-group">
              <label>Date</label>
              <input
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            <div className="form-group">

              <label>Party</label>

              <select
                className="form-control"
                value={form.customer_id}
                onChange={e => setForm({ ...form, customer_id: e.target.value })}
              >

                <option value="">Select Party</option>

                {customers
                  .filter(c => c.type === 'Credit')
                  .map(c =>
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  )}

              </select>

            </div>

            <div className="form-group">
              <label>Bill No.</label>
              <input
                className="form-control"
                value={form.bill_no}
                onChange={e => setForm({ ...form, bill_no: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Product</label>
              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                <option>HSD</option>
                <option>MS</option>
                <option>CNG</option>
              </select>
            </div>

            <div className="form-group">
              <label>Qty (L)</label>
              <input
                type="number"
                className="form-control"
                value={form.qty}
                onChange={e => setForm({ ...form, qty: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Rate (₹)</label>
              <input
                type="number"
                className="form-control"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
              />
            </div>

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>

            <button className="btn btn-primary" onClick={handleSave}>
  <i className="bi bi-save me-1"></i> Save
</button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
            >
              ✕ Cancel
            </button>

          </div>

        </div>

      )}

      <div className="section-card">

        <div className="section-header">
          Credit Sales — {records.length} records
        </div>

        <div className="table-wrapper">

          <table className="data-table">

            <thead>

              <tr>
                <th className="text-center">Date</th>
                <th className="text-left">Party</th>
                <th className="text-center">Product</th>
                <th className="text-right">Qty (L)</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-right">Paid (₹)</th>
                <th className="text-right">Balance (₹)</th>
                <th className="text-center">Action</th>
              </tr>

            </thead>

            <tbody>

              {records.map((r, i) => (

                <tr key={r.id}>
                  <td className="text-center">{r.date}</td>
                  <td className="text-left" style={{ fontWeight: 600 }}>{r.party}</td>
                  <td className="text-center">
                    <span className="badge badge-info">{r.product}</span>
                  </td>
                  <td className="text-right">{parseFloat(r.qty || 0).toFixed(3)}</td>
                  <td className="text-right">₹{parseFloat(r.rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right">₹{parseFloat(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right" style={{ color: 'var(--secondary)' }}>₹{parseFloat(r.paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right" style={{ color: parseFloat(r.balance) > 0 ? 'var(--danger)' : 'var(--secondary)', fontWeight: 700 }}>
                    ₹{parseFloat(r.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="text-center">
                    <button className="icon-btn text-info" title="Follow up">
                      <i className="bi bi-telephone-outbound"></i>
                    </button>
                  </td>
                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );

}